import React, { useMemo } from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 11,
    fontFamily: "Helvetica"
  },
  header: {
    marginBottom: 15,
    borderBottom: "1 solid #ccc",
    paddingBottom: 5
  },
  title: {
    fontSize: 18,
    textAlign: "center",
    fontWeight: "bold",
    color: "#04395b"
  },
  date: {
    fontSize: 10,
    textAlign: "center",
    color: "#666"
  },
  patientInfo: {
    marginBottom: 15,
    border: "1 solid #ddd",
    padding: 8,
    borderRadius: 4,
    backgroundColor: "#f9f9f9"
  },
  sectionTitle: {
    fontSize: 14,
    marginVertical: 6,
    color: "#04395b",
    fontWeight: "bold",
    borderBottom: "1 solid #ccc",
    paddingBottom: 3
  },
  table: {
    display: "table",
    width: "auto",
    marginBottom: 10
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#e5f0f9",
    borderBottom: "1 solid #ccc"
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: "1 solid #eee"
  },
  cellHeader: {
    flex: 1,
    padding: 4,
    fontWeight: "bold",
    fontSize: 10
  },
  cell: {
    flex: 1,
    padding: 4,
    fontSize: 10
  },
  entryBox: {
    marginVertical: 4,
    padding: 6,
    border: "1 solid #eee",
    borderRadius: 3,
    backgroundColor: "#fcfcfc"
  },
  footer: {
    position: "absolute",
    fontSize: 9,
    bottom: 20,
    left: 30,
    right: 30,
    textAlign: "center",
    color: "#999"
  }
});


const formatDate = (dateValue) => {
  if (!dateValue || dateValue === "N/A") return "N/A"; // No date
  const date = new Date(dateValue);
  return isNaN(date) ? "N/A" : date.toLocaleDateString();
};


function groupAndSortByType(history) {
  const grouped = {};

  history.forEach((item) => {
    const type = item.type || "Other";
    if (!grouped[type]) grouped[type] = [];

    grouped[type].push(item);
  });

  // Sort inside each type
  Object.keys(grouped).forEach(type => {
    const withDates = grouped[type].filter(item => item.date && item.date !== "N/A");
    const noDates = grouped[type].filter(item => !item.date || item.date === "N/A");

    withDates.sort((a, b) => new Date(b.date) - new Date(a.date));
    noDates.sort((a, b) => a.title.localeCompare(b.title));

    grouped[type] = [...withDates, ...noDates];
  });

  return grouped;
}


const MedicalHistoryReport = React.memo(({ history = [], user = {}, vitals = [], labs = [], allergies = [] }) => {
  const grouped = useMemo(() => groupAndSortByType(history), [history]);

  const dateGenerated = useMemo(() => new Date().toLocaleDateString(), []);
  const { name = "N/A", dob = "N/A", email = "N/A", phone = "N/A" } = user;

  return (
    <Document>
      <Page style={styles.page}>

        {/* HEADER */}
        <View style={styles.header}>
          <Text style={styles.title}>Medical History Report</Text>
        </View>

        <View style={styles.patientInfo}>
          <Text>Name: {name}</Text>
          <Text>DOB: {new Date(dob).toLocaleDateString()}</Text>
          <Text>Email: {email}</Text>
          <Text>Phone: {phone}</Text>
        </View>

        {/* VITALS */}
        {vitals.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>Vitals</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.cellHeader}>Date</Text>
                <Text style={styles.cellHeader}>Type</Text>
                <Text style={styles.cellHeader}>Value</Text>
              </View>
              {vitals.map((v) => (
                <View key={v.id} style={styles.tableRow}>
                  <Text style={styles.cell}>{formatDate(v.effective_date)}</Text>
                  <Text style={styles.cell}>{v.display_name}</Text>
                  <Text style={styles.cell}>{v.value} {v.unit}</Text>
                </View>
              ))}
            </View>
          </>
        )}

        {/* LAB RESULTS */}
        {labs.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>Lab Results</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.cellHeader}>Date</Text>
                <Text style={styles.cellHeader}>Test</Text>
                <Text style={styles.cellHeader}>Result</Text>
              </View>
              {labs.map((l) => (
                <View key={l.id} style={styles.tableRow}>
                  <Text style={styles.cell}>{formatDate(l.effective_date)}</Text>
                  <Text style={styles.cell}>{l.display_name}</Text>
                  <Text style={styles.cell}>{l.value} {l.unit}</Text>
                </View>
              ))}
            </View>
          </>
        )}

        {/* HISTORY */}
        {Object.keys(grouped).map((type) => (
          <View key={type}>
            <Text style={styles.sectionTitle}>{type}</Text>
            {grouped[type].map((event) => (
              <View key={`${type}-${event.title}`} style={styles.entryBox}>
                <Text>Date: {formatDate(event.date)}</Text>
                <Text>Title: {event.title}</Text>
                <Text>Details: {event.details}</Text>
              </View>
            ))}
          </View>
        ))}

        {/* ALLERGIES */}
        {allergies.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>Allergies</Text>
            <View style={styles.entryBox}>
              {allergies.map((allergy, index) => (
                <Text key={index} style={styles.entryLine}>
                  • {allergy}
                </Text>
              ))}
            </View>
          </>
        )}

        {/* FOOTER */}
        <Text
          style={styles.footer}
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages} • Generated on ${dateGenerated}`
          }
        />
      </Page>
    </Document>
  );
});



export default MedicalHistoryReport;
